'use client';

import type { IEnglishIqResult } from '@repo/sdk';
import { motion, useReducedMotion } from 'motion/react';
import type { EnglishIqMessagesType } from '../english-iq-messages-type';
import { useEnglishIqResultActions } from '../hooks';
import { useEnglishIqStageFocus } from '../hooks/wizard/use-english-iq-stage-focus';
import { EnglishIqScoreSummary } from './english-iq-score-summary';

interface IEnglishIqResultProps {
  locale: string;
  messages: EnglishIqMessagesType;
  onRetake: () => void;
  result: IEnglishIqResult;
}

export function EnglishIqResult({
  locale,
  messages,
  onRetake,
  result,
}: IEnglishIqResultProps): React.JSX.Element {
  const headingRef = useEnglishIqStageFocus<HTMLHeadingElement>();
  const prefersReducedMotion = useReducedMotion();
  const shareTitle = messages.share.metaTitle.replace('{score}', String(result.score));
  const { installUrl, recordInstall, share, shareLabel } = useEnglishIqResultActions(
    locale,
    result,
    messages.result.copied,
    shareTitle,
  );

  return (
    <motion.div
      animate={{ opacity: 1, scale: 1, y: 0 }}
      className="mx-auto max-w-3xl py-2 text-center sm:py-4"
      initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.96, y: 18 }}
      transition={prefersReducedMotion ? { duration: 0 } : { duration: 0.6, ease: 'easeOut' }}
    >
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet-600 dark:text-violet-300">
        {messages.result.eyebrow}
      </p>
      <h2
        aria-label={`${messages.result.title} ${result.score}`}
        className="mt-5 text-xl font-medium text-muted-foreground"
        ref={headingRef}
        tabIndex={-1}
      >
        {messages.result.title}
      </h2>
      <EnglishIqScoreSummary
        disclaimer={messages.disclaimer}
        messages={messages.result}
        result={result}
      />
      <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
        <button
          type="button"
          onClick={share}
          className="min-h-12 rounded-full bg-foreground px-7 py-3.5 font-semibold text-background transition hover:opacity-90 motion-reduce:transition-none"
        >
          {shareLabel ?? messages.result.share}
        </button>
        <a
          href={installUrl}
          onClick={recordInstall}
          className="inline-flex min-h-12 items-center justify-center rounded-full bg-violet-600 px-7 py-3.5 font-semibold text-white transition hover:opacity-90 motion-reduce:transition-none"
        >
          {messages.install.cta}
        </a>
        <button
          type="button"
          onClick={onRetake}
          className="min-h-12 rounded-full border border-foreground/15 bg-card px-7 py-3.5 font-semibold transition hover:border-foreground/30 motion-reduce:transition-none"
        >
          {messages.result.retake}
        </button>
      </div>
    </motion.div>
  );
}
